import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import "react-datepicker/dist/react-datepicker.css";
import { Row, Col } from "react-bootstrap";
import { FaPrescription } from "react-icons/fa";
import { MdOutlineModeEditOutline, MdSupervisedUserCircle, MdAccountCircle } from "react-icons/md";

import Lead from "../../shared/Lead";
import * as API from "../../api";
import Loading from "../../shared/Loading";
import Select from "../../shared/Select";
import TextArea from "../../shared/TextArea";
import SelectAssignee from "../forms/SelectAssignee";
import PriorityBadge from "../../shared/PriorityBadge";
import TextField from "../../shared/TextField";
import Patient from "../../shared/Patient";

import { Task, Settings } from "../../helpers/Contexts";

const RxRequestForm = ({ task, dispatch, isCall, formHandler }) => {
  const settings = useContext(Settings);
  const history = useHistory();
  const [error, setError] = useState("");
  const [taskSubmitted, setTaskSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const phoneRegex = RegExp(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/);

  const RxRequestSchema = Yup.object().shape({
    priority: Yup.string().oneOf(["low", "medium", "high"]).required("Priority is required"),
    preferred_contact_method: Yup.string().required("Patient preferred contact method is required"),
    notes: Yup.string(),
    assignee: Yup.string().required("Assignee is required."),
    pharmacy_name: Yup.string().required("Pharmacy name is required."),
    pharmacy_address_line_1: Yup.string().required("Address line 1 is required."),
    pharmacy_address_line_2: Yup.string(),
    pharmacy_zip_code: Yup.string()
      .required("Zip code is required")
      .matches(/^[0-9]+$/, "Must be only digits")
      .min(5, "Must be exactly 5 digits")
      .max(5, "Must be exactly 5 digits"),
    pharmacy_phone: Yup.string()
      .required("Phone number is required")
      .matches(phoneRegex, "Phone number is not valid")
      .min(10, "too short")
      .max(10, "too long"),
  });

  return (
    <>
      <Formik
        initialValues={
          task.isNew
            ? {
                patient_id: task.patient.id,
                assignee: "",
                assignee_id: "",
                assignee_support_group_id: "",
                assigner_id: settings.me.id,
                assigner_support_group_id: settings.me.support_group_id,
                priority: "",
                notes: "",
                preferred_contact_method: "",
                pharmacy_name: "",
                pharmacy_address_line_1: "",
                pharmacy_address_line_2: "",
                pharmacy_zip_code: "",
                pharmacy_phone: "",
              }
            : isCall
            ? {
                patient_id: task.patient.id,
                assignee: "",
                assignee_id: "",
                assignee_support_group_id: "",
                assigner_id: settings.me.id,
                assigner_support_group_id: settings.me.support_group_id,
                priority: "",
                notes: "",
                preferred_contact_method: "",
                pharmacy_name: "",
                pharmacy_address_line_1: "",
                pharmacy_address_line_2: "",
                pharmacy_zip_code: "",
                pharmacy_phone: "",
              }
            : {
                assignee: task.assignee.name,
                assigner_id: settings.me.id,
                assigner_support_group_id: settings.me.support_group_id,
                priority: task.priority,
                notes: task.notes,
                preferred_contact_method: task.preferred_contact_method,
                pharmacy_name: task.pharmacy_name,
                pharmacy_address_line_1: task.pharmacy_address_line_1,
                pharmacy_address_line_2: task.pharmacy_address_line_2,
                pharmacy_zip_code: task.pharmacy_zip_code,
                pharmacy_phone: task.pharmacy_phone,
              }
        }
        validateOnChange={true}
        validationSchema={RxRequestSchema}
        onSubmit={(values) => {
          if (isCall) {
            formHandler({
              task: {
                task_type: "rx_request",
                id: task.id,
                status: "open",
                patient_id: values.patient_id,
                assigner_id: values.assigner_id,
                assigner_support_group_id: values.assigner_support_group_id,
                priority: values.priority,
                preferred_contact_method: values.preferred_contact_method,
                notes: values.notes,
                pharmacy_name: values.pharmacy_name,
                pharmacy_address_line_1: values.pharmacy_address_line_1,
                pharmacy_address_line_2: values.pharmacy_address_line_2,
                pharmacy_zip_code: values.pharmacy_zip_code,
                pharmacy_phone: values.pharmacy_phone,
              },
            });
          } else {
            if (task.isNew) {
              setLoading(true);
              API.postTask({
                task: {
                  task_type: "rx_request",
                  status: "open",
                  assignee_id: values.assignee_id && values.assignee_id,
                  assignee_support_group_id:
                    values.assignee_support_group_id && values.assignee_support_group_id,
                  assigner_id: values.assigner_id,
                  assigner_support_group_id: values.assigner_support_group_id,
                  priority: values.priority,
                  patient_id: task.patient.id,
                  preferred_contact_method: values.preferred_contact_method,
                  notes: values.notes,
                  pharmacy_name: values.pharmacy_name,
                  pharmacy_address_line_1: values.pharmacy_address_line_1,
                  pharmacy_address_line_2: values.pharmacy_address_line_2,
                  pharmacy_zip_code: values.pharmacy_zip_code,
                  pharmacy_phone: values.pharmacy_phone,
                },
              })
                .then((res) => {
                  setLoading(false);
                  setTaskSubmitted(true);
                  dispatch({ type: "set_loading", payload: true });
                  history.push(`/physicians/tasks/item/${res.task.id}`);
                })
                .catch((err) => {
                  console.log(err);
                  setError(err);
                });
            } else {
              task.formHandler({
                task: {
                  id: task.id,
                  assigner_id: values.assigner_id,
                  assigner_support_group_id: values.assigner_support_group_id,
                  priority: values.priority,
                  preferred_contact_method: values.preferred_contact_method,
                  notes: values.notes,
                  pharmacy_name: values.pharmacy_name,
                  pharmacy_address_line_1: values.pharmacy_address_line_1,
                  pharmacy_address_line_2: values.pharmacy_address_line_2,
                  pharmacy_zip_code: values.pharmacy_zip_code,
                  pharmacy_phone: values.pharmacy_phone,
                },
              });
              setTaskSubmitted(true);
            }
          }
        }}
      >
        {({ values, setFieldValue, isSubmitting }) => {
          function renderForm() {
            return (
              <>
                <Select label="Priority" name="priority" value={values.priority}>
                  <option value="">{"Choose a priority level"}</option>
                  <option value={"low"}>Low</option>
                  <option value={"medium"}>Medium</option>
                  <option value={"high"}>High</option>
                </Select>
                <Select
                  label="Patient Preferred Contact Method"
                  name="preferred_contact_method"
                  value={values.preferred_contact_method}
                >
                  <option value={""}>{"Select patient preferred contact method"}</option>
                  <option value="text">{"Text"}</option>
                  <option value="phone">{"Phone"}</option>
                </Select>
                <TextArea
                  label="Notes"
                  value={values.notes}
                  name="notes"
                  placeholder="Write additional notes here"
                />{" "}
                <br /> <br />
                <b className="lead2">{"Patient Pharmacy Details"} </b>
                <Row className="flex">
                  <Col md={6} id="field-left">
                    {" "}
                    <TextField
                      label={"Pharmacy Name"}
                      value={values.pharmacy_name}
                      name="pharmacy_name"
                      placeholder="Pharmacy name"
                    />
                  </Col>
                  <Col md={6} id="field-right">
                    <TextField
                      value={values.pharmacy_phone}
                      label={"Pharmacy Phone"}
                      name="pharmacy_phone"
                      placeholder="Pharmacy phone number"
                    />
                  </Col>
                </Row>
                <TextField
                  value={values.pharmacy_address_line_1}
                  name="pharmacy_address_line_1"
                  label={"Pharmacy Address"}
                  placeholder="Address line 1"
                />
                <TextField
                  className="mt10 default"
                  value={values.pharmacy_address_line_2}
                  name="pharmacy_address_line_2"
                  placeholder="Address line 2"
                />
                <TextField
                  value={values.pharmacy_zip_code}
                  name="pharmacy_zip_code"
                  label={"Pharmacy Zip code"}
                  placeholder="Pharmacy zip code"
                />{" "}
              </>
            );
          }

          function renderFormVals() {
            return (
              <>
                <div className="form-values">
                  <Lead message={task.type || task.label} />
                  <br />
                  <label>Priority</label>{" "}
                  {values.priority && <PriorityBadge priority={values.priority} />}{" "}
                  <label>Patient Preferred Contact Method</label>
                  {values.preferred_contact_method && (
                    <>
                      {values.preferred_contact_method.charAt(0).toUpperCase() +
                        values.preferred_contact_method.slice(1)}
                    </>
                  )}
                  <label>Notes</label>
                  <div>{values.notes}</div>
                  <b className="lead2">{"Patient Pharmacy Details"} </b>
                  <label>Pharmacy Name</label>
                  {values.pharmacy_name}
                  <label>Pharmacy Phone</label>
                  {values.pharmacy_phone && <>{values.pharmacy_phone}</>}
                  <label>Pharmacy Address</label>
                  {values.pharmacy_address_line_1}
                  <br />
                  {values.pharmacy_address_line_2}
                  <label>Pharmacy Zip code</label>
                  {values.pharmacy_zip_code}
                </div>
                <FaPrescription id="appointment-big-icon" />
              </>
            );
          }
          function renderNewTaskForm() {
            return (
              <Form>
                <Col md={5} className=" form">
                  {!loading && taskSubmitted && <Lead message={"Task Submit Success! "} />}
                  {loading && !taskSubmitted && <Loading message={"Creating Task "} />}
                  {error && <Lead message={"Error processing request"} />}
                  <Lead message={"New " + task.type} />
                  <SelectAssignee name="assignee" setFieldValue={setFieldValue} />
                  {renderForm()}{" "}
                  <button id="btn outline" type="submit" disabled={isSubmitting}>
                    Submit Rx Request
                  </button>
                </Col>
                <Col md={7}>
                  {" "}
                  <div className="border-container">
                    <Patient patient={{ ...task.patient }} />
                  </div>
                  <div className={"border-container"}>
                    <label className="agent">
                      {"Assignee "}{" "}
                      {values.assignee && (
                        <span id="from">
                          {" "}
                          {values.assignee_id && values.assignee_support_group_id && (
                            <>
                              <MdAccountCircle className="task-agent" />
                              <b> {values.assignee}</b>
                            </>
                          )}
                          {!values.assignee_id && values.assignee_support_group_id && (
                            <>
                              {" "}
                              <MdSupervisedUserCircle className="task-agent" />
                              <b>{values.assignee} </b>
                            </>
                          )}
                        </span>
                      )}
                    </label>
                    <hr />
                    {renderFormVals()}
                    <br />
                    <br />
                  </div>
                </Col>
              </Form>
            );
          }
          function renderEditTaskForm() {
            const { taskValues, setTaskValues } = useContext(Task);

            return (
              <>
                {task.actions}
                <Col md={9}>
                  <Row className="flex">
                    {taskValues.editing.requested && (
                      <Col md={6} className="pad20">
                        <Form>
                          <MdOutlineModeEditOutline id="pencil-icon" />
                          <Lead message={"Editing Rx Request"} />

                          {renderForm()}
                          <button id="btn outline" type="submit">
                            Submit Rx Request
                          </button>
                        </Form>
                      </Col>
                    )}
                    <Col md={taskValues.editing.requested ? 6 : null} className={"pad20"}>
                      {renderFormVals()}
                      <div className="bottom-right">
                        Created at:{" "}
                        <b>
                          {new Date(task.created_at).toLocaleString([], {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </b>
                      </div>
                    </Col>{" "}
                  </Row>
                </Col>
              </>
            );
          }

          return (
            <>
              <Row className="flex">
                {(task.isNew || isCall) && renderNewTaskForm()}
                {!task.isNew && !isCall && renderEditTaskForm()}
              </Row>
            </>
          );
        }}
      </Formik>
    </>
  );
};
export default RxRequestForm;
